import { track } from "jiffy-analytics";
import { notifyError } from "common/utils/app-monitoring/notify";

/**
 * Customer Request Form Tracker
 *
 * This module handles tracking of customer request initiated events to Mixpanel.
 * It includes deduplication logic to prevent multiple events from being fired
 * for the same request when users navigate through multi-page flows.
 *
 * Deduplication works by:
 * 1. Creating a unique key for each request (type + order_number + source)
 * 2. Storing tracked requests in sessionStorage with timestamps
 * 3. Checking if a request was already tracked before firing new events
 * 4. Automatically expiring tracked requests after 5 seconds
 */

// Initialize window.jiffy object
window.jiffy = window.jiffy || {};

const DEFAULT_AUTOMATION_COUNTS = { total: 0, specific: 0 };

const IMMEDIATE_TICKET_REQUESTS = [
  "contactus_more",
  "what_is_my_tracking_number",
  "where_is_my_order",
  "contactus_bill",
  "add_to_order"
];
const TRACKED_REQUESTS_KEY = "customerRequestsTracked";
const TRACKING_TIMEOUT_MS = 5000; // 5 seconds

const CustomerRequestFormTracker = {
  /**
   * Safely parse JSON data with error handling
   * @param {string} jsonString - The JSON string to parse
   * @param {string} context - Context for error logging
   * @returns {object|null} Parsed object or null if parsing fails
   */
  parseJsonSafely(jsonString, context) {
    try {
      return JSON.parse(jsonString);
    } catch (error) {
      notifyError(`Failed to parse ${context}`, { context: { error } });
      return null;
    }
  },

  /**
   * Extract automation counts from data object or string
   * @param {object|string} source - The data source containing automation counts
   * @returns {object} Automation counts with total and specific values
   */
  getAutomationCounts(source) {
    if (typeof source === "string") {
      return this.parseJsonSafely(source, "automation counts") || DEFAULT_AUTOMATION_COUNTS;
    }
    return source?.automation_counts || DEFAULT_AUTOMATION_COUNTS;
  },

  /**
   * Generate a unique tracking key for deduplication
   * @param {object} eventData - The event data
   * @returns {string} Unique key for this request
   */
  getTrackingKey(eventData) {
    return `${eventData.type}_${eventData.order_number}_${eventData.source || "unknown"}`;
  },

  /**
   * Check if this request was already tracked
   * @param {string} trackingKey - The unique tracking key
   * @returns {boolean} True if already tracked, false otherwise
   */
  isAlreadyTracked(trackingKey) {
    try {
      const trackedRequests = JSON.parse(sessionStorage.getItem(TRACKED_REQUESTS_KEY) || "{}");
      const currentTime = Date.now();

      // Clean up old entries (older than timeout)
      Object.keys(trackedRequests).forEach(key => {
        if (currentTime - trackedRequests[key] > TRACKING_TIMEOUT_MS) {
          delete trackedRequests[key];
        }
      });

      // Save cleaned up list
      sessionStorage.setItem(TRACKED_REQUESTS_KEY, JSON.stringify(trackedRequests));

      return !!trackedRequests[trackingKey];
    } catch (error) {
      notifyError("Error checking tracked requests", { context: { error } });
      return false;
    }
  },

  /**
   * Mark a request as tracked
   * @param {string} trackingKey - The unique tracking key
   */
  markAsTracked(trackingKey) {
    try {
      const trackedRequests = JSON.parse(sessionStorage.getItem(TRACKED_REQUESTS_KEY) || "{}");
      trackedRequests[trackingKey] = Date.now();
      sessionStorage.setItem(TRACKED_REQUESTS_KEY, JSON.stringify(trackedRequests));
    } catch (error) {
      notifyError("Error marking request as tracked", { context: { error } });
    }
  },

  /**
   * Send customer request tracking event
   * @param {object} baseData - Base tracking data
   * @param {object} automationCounts - Automation counts
   */
  sendTrackingEvent(baseData, automationCounts) {
    const isImmediateTicket = IMMEDIATE_TICKET_REQUESTS.includes(baseData.type);
    const takenToFormPage = baseData.taken_to_form_page ?? !isImmediateTicket;
    const ticketCreated = baseData.ticket_created ?? isImmediateTicket;

    const eventData = {
      ...baseData,
      taken_to_form_page: takenToFormPage,
      ticket_created: ticketCreated,
      ticket_number: null,
      ticket_subject_line: null,
      number_of_total_automations: automationCounts.total,
      number_of_specific_request_automated: automationCounts.specific
    };

    // Check if this specific request was already tracked
    const trackingKey = this.getTrackingKey(eventData);
    if (this.isAlreadyTracked(trackingKey)) {
      window.jiffy.customerRequestTracked = true;
      return;
    }

    track("customer_request_initiated", eventData);

    // Mark this request as tracked
    this.markAsTracked(trackingKey);

    // Set flag to prevent duplicate tracking from fallback
    window.jiffy.customerRequestTracked = true;
  },

  /**
   * Handle tracking from data attributes
   */
  handleDataAttributeTracking() {
    const trackingElement = document.querySelector("[data-customer-request-form]");
    if (!trackingElement) return;

    const formData = this.parseJsonSafely(
      trackingElement.dataset.customerRequestForm,
      "customer request form data"
    );

    if (!formData) return;

    const automationCounts = this.getAutomationCounts(
      trackingElement.dataset.automationCounts || '{"total": 0, "specific": 0}'
    );

    this.sendTrackingEvent(formData, automationCounts);
  },

  /**
   * Initialize customer request tracking
   */
  init() {
    this.handleDataAttributeTracking();
  }
};

export default CustomerRequestFormTracker;
